/**
 * Blok Logboek in Instellingen > Meer. CC7 (besluit Frank 24 oktober 2026).
 *
 * Het spoor, de handelingsrij en de koppelingsaanroepen SAMEN, nieuwste eerst, ALLEEN LEZEN:
 * er staat geen enkele knop in die iets besluit of wijzigt. Het laadt pas op een klik, zodat
 * Instellingen openen niets kost.
 */
import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

export const SOORTEN = [
  { id: "", naam: "Alles" },
  { id: "spoor", naam: "Sporen" },
  { id: "handeling", naam: "Handelingen" },
  { id: "koppeling", naam: "Koppelingen" },
];
const SOORTNAAM = { spoor: "Spoor", handeling: "Handeling", koppeling: "Koppeling" };

/** "24-10 11:42" in de taal en zone van het scherm. PUUR. */
export function wanneer(iso, taal) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(taal === "en" ? "en-GB" : "nl-NL",
    { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function LogboekBlok() {
  const { t, taal } = useTaal();
  const [soort, setSoort] = useState("");
  const [regels, setRegels] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");

  const laad = async (welke = soort) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.get(`${API}/logboek`, { params: { soort: welke, limiet: 200 } });
      setRegels(r.data?.regels || []);
    } catch (e) {
      setFout(e.response?.data?.detail || t("Het logboek kon niet worden geladen."));
    } finally {
      setBezig(false);
    }
  };

  const kies = (id) => { setSoort(id); if (regels !== null) laad(id); };

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="logboek-blok">
      <div className="space-y-1">
        <h4 className="font-medium text-sm">{t("Logboek")}</h4>
        <p className="text-xs text-muted-foreground">
          {t("Alles wat er is gedaan op een plek: sporen van chats en agents, handelingen op taken en mail, en aanroepen van koppelingen. Alleen lezen.")}
        </p>
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label={t("Soort")}>
        {SOORTEN.map((s) => (
          <Button key={s.id || "alles"} size="sm" variant={soort === s.id ? "default" : "outline"}
                  className="h-7 text-xs" onClick={() => kies(s.id)} data-testid={`logboek-soort-${s.id || "alles"}`}>
            {t(s.naam)}
          </Button>
        ))}
        <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={() => laad()} disabled={bezig}
                data-testid="logboek-toon">
          {bezig && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          {regels === null ? t("Toon logboek") : t("Ververs")}
        </Button>
      </div>
      {fout && <p className="text-xs text-destructive">{fout}</p>}
      {regels !== null && (regels.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="logboek-leeg">{t("Nog niets in het logboek.")}</p>
      ) : (
        // GL200-5 (glasronde 0.9.200): lange regels liepen rechts weg. De tekst loopt nu door
        // op een tweede regel, en op een smal scherm staat hij onder tijd en soort. Gemeten in
        // een echte browser op vijf breedtes, ook 390 (`meting/opmaak.mjs`, het Logboek).
        <ul className="max-h-80 overflow-y-auto overflow-x-hidden divide-y rounded-md border text-xs" data-testid="logboek-lijst">
          {regels.map((r, i) => (
            <li key={`${r.soort}-${r.wanneer}-${i}`} className="px-3 py-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5" data-testid="logboek-regel">
              <span className="tabular-nums text-muted-foreground shrink-0 w-20">{wanneer(r.wanneer, taal)}</span>
              <span className="shrink-0 w-20">{t(SOORTNAAM[r.soort] || r.soort)}</span>
              <span className="min-w-0 basis-full sm:basis-0 sm:flex-1 break-words [overflow-wrap:anywhere]" data-testid="logboek-wat">
                {r.wie ? `${r.wie}: ` : ""}{r.wat}
              </span>
              <span className="min-w-0 text-muted-foreground break-words [overflow-wrap:anywhere]" data-testid="logboek-uitkomst">{r.uitkomst}</span>
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}
